// ============================================================================
// Hub layouts (Option D). A hub is a persistent surface — eyebrow + title +
// a role-filtered tab strip — that wraps several related modules so they live
// behind one door (People · Money · Admin). The active module renders in the
// <Outlet/>; its own page header shows beneath the hub chrome.
//
// RoutesLayout is the same idea for the flat "My Route" entry, which subsumes
// Today / Template / Team Routes as tabs.
//
// Tabs and overview cards read the shared nav-config tree, so role gating and
// the ⌘K palette (PR-3) all agree on one source of truth.
// ============================================================================

import { hasFullAmAuthority } from './role-gates';
import { Navigate, NavLink, Outlet } from 'react-router-dom';
import { useAuth } from './auth';
import {
  HUBS,
  hubIndexVisible,
  visibleHubChildren,
  type HubKey,
  type NavLeaf,
} from './nav-config';
import { useNavBadges } from './use-nav-badges';
import { useCanAccessSourcing } from './use-can-access-sourcing';
import { EmptyState } from './components';

// ----- route-aware tab strip -------------------------------------------------

interface TabSpec {
  to: string;
  label: string;
  end?: boolean;
  count?: number;
}

function HubTabs({ tabs }: { tabs: TabSpec[] }) {
  return (
    <div role="tablist" className="mt-4 flex gap-1 overflow-x-auto border-b border-tcs-border">
      {tabs.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end={t.end}
          role="tab"
          className={({ isActive }) =>
            [
              '-mb-px inline-flex shrink-0 items-center gap-2 border-b-2 px-3 py-2 text-sm transition-colors',
              isActive
                ? 'border-tcs-text font-medium text-tcs-text'
                : 'border-transparent text-tcs-text-secondary hover:text-tcs-text',
            ].join(' ')
          }
        >
          <span>{t.label}</span>
          {typeof t.count === 'number' && t.count > 0 && (
            <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-st-warn-bg px-1 text-[10px] font-semibold leading-none text-st-warn-fg">
              {t.count}
            </span>
          )}
        </NavLink>
      ))}
    </div>
  );
}

// ----- hub layout ------------------------------------------------------------

export function HubLayout({ hubKey }: { hubKey: HubKey }) {
  const { profile } = useAuth();
  const role = profile?.role;
  const badges = useNavBadges();
  const { canAccess: canAccessSourcing } = useCanAccessSourcing();
  const hub = HUBS[hubKey];
  const children = visibleHubChildren(role, hub, { sourcing: canAccessSourcing });

  // A hub with its own first tab (People's Desk, item 818) shows it only to the
  // roles that hold it and shows nobody a generic Overview; the other hubs
  // keep theirs. `end` keeps the index tab from lighting up on every child.
  const indexTab: TabSpec[] = hub.index
    ? hubIndexVisible(role, hub)
      ? [{ to: hub.basePath, label: hub.index.label, end: true }]
      : []
    : [{ to: hub.basePath, label: 'Overview', end: true }];
  const tabs: TabSpec[] = [
    ...indexTab,
    ...children.map((c) => ({
      to: c.path,
      label: c.label,
      count: c.badgeKey ? badges[c.badgeKey] : undefined,
    })),
  ];

  return (
    <div className="flex min-h-full flex-col">
      <div className="border-b border-tcs-border bg-tcs-bg px-4 pt-5 md:px-6">
        <div className="font-mono text-[10px] uppercase tracking-wide text-tcs-text-muted">
          {hub.eyebrow}
        </div>
        <h1 className="mt-1 text-xl font-semibold text-tcs-text">{hub.label}</h1>
        <HubTabs tabs={tabs} />
      </div>
      <div className="min-h-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
}

// ----- hub overview ----------------------------------------------------------

export function HubOverview({ hubKey }: { hubKey: HubKey }) {
  const { profile } = useAuth();
  const role = profile?.role;
  const badges = useNavBadges();
  const { canAccess: canAccessSourcing } = useCanAccessSourcing();
  const hub = HUBS[hubKey];
  const children = visibleHubChildren(role, hub, { sourcing: canAccessSourcing });

  if (children.length === 0) {
    return (
      <div className="p-6">
        <EmptyState title="Nothing here for your role" body="You don't have access to any modules in this hub." />
      </div>
    );
  }

  return (
    <div className="grid gap-4 p-6 md:grid-cols-2">
      {children.map((c) => (
        <OverviewCard key={c.path} leaf={c} count={c.badgeKey ? badges[c.badgeKey] : 0} />
      ))}
    </div>
  );
}

function OverviewCard({ leaf, count }: { leaf: NavLeaf; count: number }) {
  const Icon = leaf.icon;
  return (
    <NavLink
      to={leaf.path}
      className="group rounded-lg border border-tcs-border bg-tcs-bg p-4 transition-colors hover:bg-tcs-bg-muted"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-tcs-text">
          <Icon className="h-4 w-4 text-tcs-text-secondary" />
          {leaf.label}
        </div>
        {count > 0 && (
          <span className="rounded-full bg-st-warn-bg px-2 py-0.5 text-[10px] font-semibold text-st-warn-fg">
            {count} to review
          </span>
        )}
      </div>
      {leaf.blurb && <p className="mt-1 text-xs text-tcs-text-secondary">{leaf.blurb}</p>}
      <p className="mt-2 text-xs text-tcs-accent group-hover:underline">Open →</p>
    </NavLink>
  );
}

// ----- per-hub bound wrappers (router-friendly element props) ----------------

export const PeopleHub = () => <HubLayout hubKey="people" />;
export const MoneyHub = () => <HubLayout hubKey="money" />;
export const AdminHub = () => <HubLayout hubKey="admin" />;

/**
 * Where /people itself lands when the role has no Desk: the first tab that
 * role can see. An account manager opens People and is on Hiring. A role
 * with nothing in the hub gets the empty state, as before.
 */
export function HubFirstChildRedirect({ hubKey }: { hubKey: HubKey }) {
  const { profile } = useAuth();
  const { canAccess: canAccessSourcing } = useCanAccessSourcing();
  const first = visibleHubChildren(profile?.role, HUBS[hubKey], { sourcing: canAccessSourcing })[0];
  if (!first) {
    return (
      <div className="p-6">
        <EmptyState title="Nothing here for your role" body="You don't have access to any modules in this hub." />
      </div>
    );
  }
  return <Navigate to={first.path} replace />;
}
export const MoneyOverview = () => <HubOverview hubKey="money" />;
export const AdminOverview = () => <HubOverview hubKey="admin" />;

// ----- My Route layout (Today / Template / Team Routes as tabs) --------------

export function RoutesLayout() {
  const { profile } = useAuth();
  const role = profile?.role;
  const canSeeTeam = hasFullAmAuthority(role);

  const tabs: TabSpec[] = [
    { to: '/route', label: 'Today', end: true },
    { to: '/route/template', label: 'Template' },
    ...(canSeeTeam ? [{ to: '/route/team', label: 'Team Routes' }] : []),
  ];

  return (
    <div className="flex min-h-full flex-col">
      <div className="border-b border-tcs-border bg-tcs-bg px-4 pt-5 md:px-6">
        <div className="font-mono text-[10px] uppercase tracking-wide text-tcs-text-muted">
          My Route · daily rotation
        </div>
        <h1 className="mt-1 text-xl font-semibold text-tcs-text">My Route</h1>
        <HubTabs tabs={tabs} />
      </div>
      <div className="min-h-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
}
